import { createAsyncThunk } from "@reduxjs/toolkit";
import moment from "moment";
import { protectedApi } from "../../app/utils/common/protectedApi";
import { APIS } from "../../app/utils/constants/apiConstant";

export const getAttendanceCalendarAPI = createAsyncThunk(
  "attendance/getAttendanceCalendarAPI",
  async ({ startDate, endDate, userId }, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: `${APIS.ATTENDANCE_CALENDAR}?startDate=${startDate}&endDate=${endDate}&userId=${userId || "me"}`,
        method: "GET",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const syncAttendanceAPI = createAsyncThunk(
  "attendance/syncAttendanceAPI",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.ATTENDANCE_SYNC,
        method: "POST",
        data: payload,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

/** Fetches today's logs for current user only; stored separately from attendance page `logs`. */
export const getDashboardTodayAttendanceAPI = createAsyncThunk(
  "attendance/getDashboardTodayAttendanceAPI",
  async (_, { rejectWithValue }) => {
    const today = moment().format("YYYY-MM-DD");
    try {
      const params = new URLSearchParams();
      params.append("startDate", today);
      params.append("endDate", today);
      params.append("userId", "me");
      params.append("page", "1");
      params.append("limit", "200");
      const response = await protectedApi({
        url: `${APIS.ATTENDANCE_LOGS}?${params.toString()}`,
        method: "GET",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

/** Yesterday’s punches for dashboard shortfall tile (current user only). */
export const getDashboardYesterdayAttendanceAPI = createAsyncThunk(
  "attendance/getDashboardYesterdayAttendanceAPI",
  async (_, { rejectWithValue }) => {
    const y = moment().subtract(1, "day").format("YYYY-MM-DD");
    try {
      const params = new URLSearchParams();
      params.append("startDate", y);
      params.append("endDate", y);
      params.append("userId", "me");
      params.append("page", "1");
      params.append("limit", "200");
      const response = await protectedApi({
        url: `${APIS.ATTENDANCE_LOGS}?${params.toString()}`,
        method: "GET",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const getAttendanceLogsAPI = createAsyncThunk(
  "attendance/getAttendanceLogsAPI",
  async ({ startDate, endDate, userId, employeeCode, source, page = 1, limit = 50 }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      params.append("userId", userId || "me");
      if (employeeCode) params.append("employeeCode", employeeCode);
      if (source) params.append("source", source);
      params.append("page", String(page));
      params.append("limit", String(limit));

      const response = await protectedApi({
        url: `${APIS.ATTENDANCE_LOGS}?${params.toString()}`,
        method: "GET",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const getAttendanceConfigAPI = createAsyncThunk(
  "attendance/getAttendanceConfigAPI",
  async (_, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.ATTENDANCE_CONFIG,
        method: "GET",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const getAttendanceWebhookStatusAPI = createAsyncThunk(
  "attendance/getAttendanceWebhookStatusAPI",
  async (_, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.ATTENDANCE_WEBHOOK_STATUS,
        method: "GET",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const updateAttendanceConfigAPI = createAsyncThunk(
  "attendance/updateAttendanceConfigAPI",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.ATTENDANCE_CONFIG,
        method: "PUT",
        data: payload,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const getMachineUsersAPI = createAsyncThunk(
  "attendance/getMachineUsersAPI",
  async (_, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.ATTENDANCE_MACHINE_USERS,
        method: "GET",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const createMachineUserAPI = createAsyncThunk(
  "attendance/createMachineUserAPI",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.ATTENDANCE_MACHINE_USERS,
        method: "POST",
        data: payload,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const updateMachineUserAPI = createAsyncThunk(
  "attendance/updateMachineUserAPI",
  async ({ employeeCode, payload }, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: `${APIS.ATTENDANCE_MACHINE_USERS}/${encodeURIComponent(employeeCode)}`,
        method: "PUT",
        data: payload,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const deleteMachineUserAPI = createAsyncThunk(
  "attendance/deleteMachineUserAPI",
  async (employeeCode, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: `${APIS.ATTENDANCE_MACHINE_USERS}/${encodeURIComponent(employeeCode)}`,
        method: "DELETE",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const syncDashboardUserToMachineAPI = createAsyncThunk(
  "attendance/syncDashboardUserToMachineAPI",
  async (userId, { rejectWithValue }) => {
    try {
      const url = APIS.ATTENDANCE_SYNC_DASHBOARD_USER_TO_MACHINE.replace(":userId", userId);
      const response = await protectedApi({
        url,
        method: "POST",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const getAttendanceDailyAnalyticsAPI = createAsyncThunk(
  "attendance/getAttendanceDailyAnalyticsAPI",
  async ({ from, to, userId, view = "overview", scope }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ from, to });
      if (userId != null && userId !== "") params.append("userId", String(userId));
      if (scope === "all") params.append("scope", "all");
      const response = await protectedApi({
        url: `${APIS.ATTENDANCE_ANALYTICS_DAILY}?${params.toString()}`,
        method: "GET",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const getAttendanceTeamWeekAPI = createAsyncThunk(
  "attendance/getAttendanceTeamWeekAPI",
  async ({ weekStart }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ weekStart });
      const response = await protectedApi({
        url: `${APIS.ATTENDANCE_ANALYTICS_TEAM_WEEK}?${params.toString()}`,
        method: "GET",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const getAttendanceHolidaysAPI = createAsyncThunk(
  "attendance/getAttendanceHolidaysAPI",
  async (_, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.ATTENDANCE_HOLIDAYS,
        method: "GET",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const createAttendanceHolidayAPI = createAsyncThunk(
  "attendance/createAttendanceHolidayAPI",
  async ({ holidayDate, label }, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.ATTENDANCE_HOLIDAYS,
        method: "POST",
        data: { holidayDate, label },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const deleteAttendanceHolidayAPI = createAsyncThunk(
  "attendance/deleteAttendanceHolidayAPI",
  async (id, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: `${APIS.ATTENDANCE_HOLIDAYS}/${id}`,
        method: "DELETE",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);

export const putAttendanceDayOverrideAPI = createAsyncThunk(
  "attendance/putAttendanceDayOverrideAPI",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await protectedApi({
        url: APIS.ATTENDANCE_DAY_OVERRIDE,
        method: "PUT",
        data: payload,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || error);
    }
  }
);
